import { Loader2, X } from 'lucide-react';
import { type ReactElement, useCallback, useMemo } from 'react';
import { Column, Getter, Row } from '@tanstack/react-table';

import { cn } from '@crossnokaye/ux-common';

import { TooltipButton } from '@/components/molecules/tooltip-button';
import {
  ActivationsDataTableColumnMeta,
  AlarmTableData,
} from '@/features/alarms/alarm-tables/types';
import { useClearAlarmActivations } from '@/hooks/query-hooks/alarm-activations';
import {
  DeviceTypeDetectionVariant,
  useDeviceTypeDetection,
} from '@/ux-common/hooks/use-device-type-detection/use-device-type-detection';

export const ActionClearCell = <TData extends AlarmTableData, TValue>({
  column,
  getValue,
  row,
}: {
  column: Column<TData, TValue>;
  getValue: Getter<TValue>;
  row: Row<TData>;
}): ReactElement => {
  const meta = column.columnDef.meta as ActivationsDataTableColumnMeta<TData, TValue>;
  const value = getValue() as string;
  const { isMobile } = useDeviceTypeDetection({
    variant: DeviceTypeDetectionVariant.DeviceOnly,
  });

  const mutation = useClearAlarmActivations({
    orgId: meta?.organizationId,
    agentId: meta?.agentId,
  });

  const isDisabled = useMemo(() => {
    return meta.disabled || !row.original.resolvedAt;
  }, [meta.disabled, row.original.resolvedAt]);

  const handleClick = useCallback(() => {
    mutation?.mutate([row.original.activationId]);
  }, [mutation, row.original.activationId]);

  const isLoading = useMemo(() => {
    return mutation?.isPending || meta.isLoading;
  }, [mutation?.isPending, meta.isLoading]);

  return (
    <div className={'flex justify-center w-full p-2'}>
      {isMobile ? (
        <TooltipButton
          disabled={isDisabled}
          tooltipContent={meta?.tooltipText}
          onClick={handleClick}
          className="btn btn-tier2"
        >
          {isLoading ? (
            <Loader2
              className={cn(
                'glyph glyph-sm text-foreground animate-spin',
                isDisabled && 'text-muted-foreground'
              )}
              aria-label="Loading"
            />
          ) : (
            <X
              className={cn(
                'glyph glyph-sm text-foreground',
                isDisabled && 'text-muted-foreground'
              )}
              aria-hidden="true"
            />
          )}
        </TooltipButton>
      ) : (
        <TooltipButton
          disabled={isDisabled}
          tooltipContent={meta?.tooltipText}
          onClick={handleClick}
          className="btn btn-tier2 btn-sm"
        >
          {value}
        </TooltipButton>
      )}
    </div>
  );
};
